// HTTP wrapper that throws on !ok responses and network failures.
// See docs/guardrail-catches.md "safeFetch wrapper" and epic #878 / issue #881.
// The 2026-04-17 voice outage stayed invisible for 18h because 132 fetch sites
// silently swallowed 500s. This wrapper makes ignoring HTTP failures impossible
// by default.
//
// Issue #906: `safeFetch` and `safeAuthFetch` share body-read + emitError +
// network-wrap logic. That logic is extracted into `assertOkOrThrow` and
// `wrapNetworkErrorOrRethrow` below so the two paths can't drift.

import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { authFetch } from '../services/authFetch.js';
import { getPreferredLanguage } from '../lib/voice/languageConfig';

export class FetchError extends Error {
  status: number; // 0 = network failure (no HTTP response)
  statusText: string;
  url: string;
  method: string;
  responseBody: string | null; // truncated to ~512 chars
  // Issue #1000: parsed response body so callers can read structured error
  // payloads (e.g. `{ error, code }` from the backend) without re-parsing
  // `responseBody` themselves. Set to the parsed JSON when the body is valid
  // JSON, the raw string when it is not, or null when the body is empty /
  // unreadable. `responseBody` is preserved unchanged for telemetry callers.
  body: unknown;

  constructor(
    response: Pick<Response, 'status' | 'statusText' | 'url'>,
    responseBody: string | null,
    method: string,
    options?: { cause?: unknown },
  ) {
    super(
      `HTTP ${response.status} ${response.statusText || 'Network error'} on ${method} ${response.url}`,
      options as ErrorOptions,
    );
    this.name = 'FetchError';
    this.status = response.status;
    this.statusText = response.statusText;
    this.url = response.url;
    this.method = method;
    this.responseBody = responseBody;
    this.body = parseResponseBody(responseBody);
  }
}

// Best-effort body parser used by FetchError. Returns the parsed JSON when
// the string is valid JSON, the raw string when it is not, or null when
// there is no body to parse. Never throws.
function parseResponseBody(body: string | null): unknown {
  if (body == null || body === '') return null;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

export interface SafeFetchOptions extends RequestInit {
  // Statuses for which the wrapper should NOT throw — caller handles them.
  // Example: `allowErrorStatuses: [404]` when a 404 means "no data yet".
  allowErrorStatuses?: number[];
}

function resolveUrlString(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url;
}

export async function safeFetch(
  input: RequestInfo | URL,
  init: SafeFetchOptions = {},
): Promise<Response> {
  const { allowErrorStatuses, ...fetchInit } = init;
  const method = (fetchInit.method ?? 'GET').toUpperCase();
  const urlString = resolveUrlString(input);

  let response: Response;
  try {
    // eslint-disable-next-line no-restricted-syntax -- wrapper implementation
    response = await fetch(input, fetchInit);
  } catch (err) {
    wrapNetworkErrorOrRethrow(err, urlString, method);
  }

  return assertOkOrThrow(response, urlString, method, allowErrorStatuses);
}

// Composes authFetch (which dispatches `sessionExpired` on 401 / 403-INVALID_TOKEN)
// with the ok-assertion, so the session-expiry redirect flow is preserved.
export async function safeAuthFetch(
  input: RequestInfo | URL,
  init: SafeFetchOptions = {},
): Promise<Response> {
  const { allowErrorStatuses, ...fetchInit } = init;
  const method = (fetchInit.method ?? 'GET').toUpperCase();
  const urlString = resolveUrlString(input);

  // Issue #1212: Seed X-Preferred-Language so the backend can pick up the learner's
  // UI-selected language at run creation without every POST /api/runs callsite having
  // to thread it explicitly. Caller-supplied header always wins.
  const withLanguageHeader = withPreferredLanguageHeader(fetchInit);

  let response: Response;
  try {
    // eslint-disable-next-line no-restricted-syntax -- safeAuthFetch composes authFetch
    response = await authFetch(input, withLanguageHeader);
  } catch (err) {
    wrapNetworkErrorOrRethrow(err, urlString, method);
  }

  return assertOkOrThrow(response, urlString, method, allowErrorStatuses);
}

// Issue #1212: merge `X-Preferred-Language` into the request init's headers without
// overriding a caller-supplied header. Handles all three Headers shapes (Headers,
// plain object, [name, value][] tuple list). No-op if `getPreferredLanguage()` returns
// a falsy / default value.
function withPreferredLanguageHeader(init: RequestInit): RequestInit {
  const preferred = getPreferredLanguage();
  if (!preferred) return init;

  const HEADER = 'X-Preferred-Language';
  const existing = init.headers;

  if (existing instanceof Headers) {
    if (existing.has(HEADER)) return init;
    const next = new Headers(existing);
    next.set(HEADER, preferred);
    return { ...init, headers: next };
  }

  if (Array.isArray(existing)) {
    const hasIt = existing.some(([k]) => k.toLowerCase() === HEADER.toLowerCase());
    if (hasIt) return init;
    return { ...init, headers: [...existing, [HEADER, preferred]] };
  }

  // Plain object or undefined — case-insensitive check for caller override.
  const obj = (existing as Record<string, string> | undefined) ?? {};
  const hasIt = Object.keys(obj).some(k => k.toLowerCase() === HEADER.toLowerCase());
  if (hasIt) return init;
  return { ...init, headers: { ...obj, [HEADER]: preferred } };
}

// --- Shared helpers (not exported) ---------------------------------------

// Validates the response, reads + truncates the body on error, emits
// telemetry, and throws FetchError. Returns the original response on success.
async function assertOkOrThrow(
  response: Response,
  input: string,
  method: string,
  allowErrorStatuses: number[] | undefined,
): Promise<Response> {
  if (response.ok) return response;
  if (allowErrorStatuses?.includes(response.status)) return response;

  // Read body from a clone so the caller's downstream `.json()` / `.text()`
  // still works — but callers in a catch path usually don't need it.
  const bodyText = await safeReadBodyText(response.clone());
  const truncated = bodyText ? bodyText.slice(0, 512) : null;

  // Prefer the original request URL over response.url: clearer in telemetry,
  // and response.url is empty for Responses synthesized in tests or by service workers.
  const reportedUrl = response.url || input;
  emitError({ status: response.status, url: reportedUrl, method, responseBody: truncated });
  throw new FetchError(
    { status: response.status, statusText: response.statusText, url: reportedUrl },
    truncated,
    method,
  );
}

// Wraps non-AbortError network failures in FetchError{status:0}. AbortError is
// cooperative cancellation — rethrown without telemetry. Never returns (always
// throws), hence the `never` return type — callers can place it in a catch
// block and the compiler still knows `response` is assigned after.
function wrapNetworkErrorOrRethrow(err: unknown, urlString: string, method: string): never {
  if ((err as { name?: string } | null)?.name === 'AbortError') throw err;

  emitError({ status: 0, url: urlString, method, responseBody: null });
  throw new FetchError(
    { status: 0, statusText: 'Network error', url: urlString },
    null,
    method,
    { cause: err },
  );
}

async function safeReadBodyText(response: Response): Promise<string | null> {
  try {
    return await response.text();
  } catch {
    return null;
  }
}

interface EmitErrorArgs {
  status: number;
  url: string;
  method: string;
  responseBody: string | null;
}

function emitError({ status, url, method, responseBody }: EmitErrorArgs): void {
  const redactedPath = redactUrl(url);

  try {
    Sentry.addBreadcrumb({
      category: 'fetch',
      type: 'http',
      level: status >= 500 || status === 0 ? 'error' : 'warning',
      data: {
        status_code: status,
        url: redactedPath,
        method,
        body_preview: responseBody ? responseBody.slice(0, 200) : undefined,
      },
    });
    if (status >= 500 || status === 0) {
      Sentry.captureException(
        new Error(`fetch ${status} on ${method} ${redactedPath}`),
        {
          tags: { fetch_status: String(status), fetch_method: method },
          // One Sentry issue per endpoint+status, not per resource id: the message carries
          // the id, so default grouping opened a "new" issue (and alert) per run for
          // routine network drops (CHATBOT-FRONTEND-98, run 9331).
          fingerprint: ['safeFetch', String(status), method, templatePath(redactedPath)],
        },
      );
    }
  } catch {
    // Never break the app on telemetry failure.
  }

  try {
    posthog.capture('fetch_error', {
      status,
      method,
      url_path: redactedPath,
      has_body: responseBody != null,
    });
  } catch {
    // posthog may be uninitialized in tests / SSR.
  }
}

// Replaces id-like path segments (numeric, UUID, long hex) with `:id`.
export function templatePath(path: string): string {
  return path
    .split('/')
    .map(seg => (/^\d+$/.test(seg)
      || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)
      || /^[0-9a-f]{16,}$/i.test(seg) ? ':id' : seg))
    .join('/');
}

function redactUrl(url: string): string {
  // Query strings can contain tokens, emails, search terms. Keep path only.
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return u.pathname;
  } catch {
    return url.split('?')[0];
  }
}
